import React, { useState, useEffect } from "react";
import { Paper, Typography, Grid2, Divider } from "@mui/material";
import { useForm } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";
import ControlledTextField from "@components/inputs/controlledTextField";
import ControlledRichTextEditor from "@components/inputs/controlledRichTextEditor";

import CreateConfirmDialog from "./createConfirmDialog";

import { useSnackbar } from "@context/snackbarContext";
import { useAuthentication } from "@redux/authentication/hook";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";
import { formatDateTime } from "@utils/formatter";


const schema = z.object({
  featureName: z.string().min(1, 'Featured Donation Name is required'),

  maxApplications: z.union([
    z.string()
      .min(1, "Max Applicants is required")
      .regex(/^\d+$/, "Max Applicants must be a whole number"),
    z.number()
      .int("Max Applicants must be a whole number")
      .positive("Max Applicants must be a positive number"),
  ]),

  taglineCntrad: z.string().min(1, { message: '標語 is required' }),
  headerCntrad: z.string().min(1, { message: '標頭 is required' }),
  contentCntrad: z.string().min(1, { message: '內容 is required' }),

  taglineCnsimp: z.string().min(1, { message: '标语 is required' }),
  headerCnsimp: z.string().min(1, { message: '标头 is required' }),
  contentCnsimp: z.string().min(1, { message: '內容 is required' }),

  taglineEng: z.string().min(1, { message: 'Tagline is required' }),
  headerEng: z.string().min(1, { message: 'Header is required' }),
  contentEng: z.string().min(1, { message: 'Content is required' }),

  /* view only */
  createdOn: z.string().optional(),
  createdBy: z.string().optional(),
})

const ACTION = {
  UPDATE: 'Update',
  PUBLISH: 'Publish',
  UNLIST: 'Unlist'
}

export default function FeaturedForm() {

  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const navigate = useNavigate()
  const { pathname } = useLocation();
  const { user, isCurrentModuleAdmin } = useAuthentication();
  const pageAction = pathname.split("/")[3] || null; // get from pathname expected values ["create" || "view" || "edit"]
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");


  const { handleSubmit, control, getValues, reset, watch } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
    }
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [openCreateConfirmDialog, setOpenCreateConfirmDialog] = useState(false)
  const [confirmDialogAction, setConfirmDialogAction] = useState(ACTION.CREATE)
  const [status, setStatus] = useState(false)

  useEffect(()=>{
    if (((pageAction === "edit" || pageAction === "view") && !id) || (pageAction === "create" && id)) {
      onBack()
    }
    if (id) {
      fetchFeaturedMutation.mutate(id)
    }
  },[pageAction])

  const fetchFeaturedMutation = useMutation({
    mutationFn: (id) => {
      return axios.get(`${AdminApiEndpoints.getFeaturedDonation}/${id}`)
    },
    onSuccess: (res) => {
      const {createdOn, ...data} = res.data
      // console.log(data)
      const createdOnFormatted = formatDateTime(createdOn)
      reset({...data, createdOn: createdOnFormatted})
      setStatus(data?.status)
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve featured", "error");
    }
  });
  

  const createFeaturedMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(AdminApiEndpoints.createFeaturedDonation, {...data, createdBy: user.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      setOpenCreateConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getFeatured'] })
      openSnackbar("Successfully created featured", "success");
      onBack()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      setOpenCreateConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to create featured", "error");
    }
  })

  const updateFeaturedMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.updateFeaturedDonation}/${id}`, {...data, updatedBy: user.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getFeatured'] })
      openSnackbar("Successfully updated featured", "success");
      fetchFeaturedMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to update featured", "error");
    }
  })

  const updateFeaturedStatusMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.updateFearuredDonationStatus}/${id}`, {status: data?.status, updatedBy: user?.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getFeatured'] })
      openSnackbar("Successfully updated featured status", "success");
      fetchFeaturedMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      openSnackbar("Failed to update featured status", "error");
    }
  })

  const onSubmit = () => {
    if(pageAction === "create"){
      setOpenCreateConfirmDialog(true)
      return
    }
    setConfirmDialogAction(ACTION.UPDATE)
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = ({checked = true}) => {
    const {...data} = getValues();
    
    if(pageAction === "create")
      createFeaturedMutation.mutate({...data, status: checked})
    else{
      if(confirmDialogAction === ACTION.PUBLISH){
        updateFeaturedStatusMutation.mutate({status: true})
      }
      else if(confirmDialogAction === ACTION.UNLIST){
        updateFeaturedStatusMutation.mutate({status: false})
      }
      else if(confirmDialogAction === ACTION.UPDATE){
        updateFeaturedMutation.mutate({...data})
      }
    }
      
  }

  const onBack = () => {
    navigate('/admin/featured', { replace: true });
  }

  // console.log(watch('content'))

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs 
        rightSection={
          (!fetchFeaturedMutation.isPending && !fetchFeaturedMutation.isError) && isCurrentModuleAdmin && pageAction === "edit" && 
          <div style={{display: 'flex', gap: "10px"}}>
            <CustomButton color="#4DB6AC" disabled={!!(status)} onClick={()=>{setConfirmDialogAction(ACTION.PUBLISH);setOpenConfirmDialog(true)}}>
              Publish
            </CustomButton>
            <CustomButton color="#D13F58" disabled={!(status)} onClick={()=>{setConfirmDialogAction(ACTION.UNLIST);setOpenConfirmDialog(true)}}>
              Unlist
            </CustomButton>
          </div>
        }
      />
      <SkeletonWrapper isLoading={fetchFeaturedMutation.isPending} isError={fetchFeaturedMutation.isError} errorMessage="Unable to load featured" disabled={["create"].includes(pageAction)}>
      <form onSubmit={handleSubmit(onSubmit)} style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Featured Details</Typography>
            </Grid2>
            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="featureName"
                control={control}
                label="Featured Donation Name"
                disabled={fetchFeaturedMutation.isPending || fetchFeaturedMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="maxApplications"
                control={control}
                label="Max Applicants"
                disabled={fetchFeaturedMutation.isPending || fetchFeaturedMutation.isError}
                readOnly={["view"].includes(pageAction)}
                type="number"
              />
            </Grid2>

            {["view", "edit"].includes(pageAction) && <>

            <Grid2 size={{ xs: 12 }}>
              <Divider />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdOn"
                control={control}
                label="Created On"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdBy"
                control={control}
                label="Created By"
                disabled={true}
              />
            </Grid2>

            </>}

          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>繁体中文</Typography>
            </Grid2>
            <Grid2 size={{lg: 6, md: 6, xs: 12}}>
              <ControlledTextField 
                name="headerCntrad"
                control={control}
                label="標頭"
                disabled={fetchFeaturedMutation.isPending || fetchFeaturedMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>
            <Grid2 size={{lg: 6, md: 6, xs: 12}}>
              <ControlledTextField 
                name="taglineCntrad"
                control={control}
                label="標語"
                disabled={fetchFeaturedMutation.isPending || fetchFeaturedMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>
            <Grid2 size={12}>
              <ControlledRichTextEditor 
                name="contentCntrad"
                control={control}
                disabled={["view"].includes(pageAction)}
              />
            </Grid2>
          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>简体中文</Typography>
            </Grid2>
            <Grid2 size={{lg: 6, md: 6, xs: 12}}>
              <ControlledTextField 
                name="headerCnsimp"
                control={control}
                label="标头"
                disabled={fetchFeaturedMutation.isPending || fetchFeaturedMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>
            <Grid2 size={{lg: 6, md: 6, xs: 12}}>
              <ControlledTextField 
                name="taglineCnsimp"
                control={control}
                label="标语"
                disabled={fetchFeaturedMutation.isPending || fetchFeaturedMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>
            <Grid2 size={12}>
              <ControlledRichTextEditor 
                name="contentCnsimp"
                control={control}
                disabled={["view"].includes(pageAction)}
              />
            </Grid2>
          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>English</Typography>
            </Grid2>
            <Grid2 size={{lg: 6, md: 6, xs: 12}}>
              <ControlledTextField 
                name="headerEng"
                control={control}
                label="Header"
                disabled={fetchFeaturedMutation.isPending || fetchFeaturedMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>
            <Grid2 size={{lg: 6, md: 6, xs: 12}}>
              <ControlledTextField 
                name="taglineEng"
                control={control}
                label="Tagline"
                disabled={fetchFeaturedMutation.isPending || fetchFeaturedMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>
            <Grid2 size={12}>
              <ControlledRichTextEditor 
                name="contentEng"
                control={control}
                disabled={["view"].includes(pageAction)}
              />
            </Grid2>
          </Grid2>
        </Paper>

        {["create", "edit"].includes(pageAction) && 
          <Paper
            sx={{
              p: "20px 25px",
              boxShadow: "none",
              borderRadius: "10px",
              border: "1px solid #E5E5E5",
              overflowX: "auto",
              display: "flex",
              justifyContent: 'flex-end',
              gap: "10px"
            }}
          >
            <CustomButton type="submit" disabled={createFeaturedMutation.isPending || fetchFeaturedMutation.isError}>Submit</CustomButton>
          </Paper>
        }
      </form>
      </SkeletonWrapper>
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription={`${confirmDialogAction} featured?`}
        onSubmit={()=>onConfirmSubmit({checked: false})}
        loading={createFeaturedMutation.isPending || updateFeaturedMutation.isPending}
      />
      <CreateConfirmDialog 
        open={openCreateConfirmDialog}
        onClose={()=>setOpenCreateConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription="Are you sure you want to create this donation?"
        onSubmit={onConfirmSubmit}
        loading={createFeaturedMutation.isPending || updateFeaturedMutation.isPending}
      />
    </div>
  );
}
